import { t } from '@lingui/macro';
import { Trans } from '@lingui/macro';
import Radio from '@mos-cat/radio';
import { Heading } from '@mos-cat/typography';
import { connect } from 'dva';
import React, { ChangeEvent, ChangeEventHandler, useState } from 'react';
import { Dispatch } from 'redux';
import { createSelector } from 'reselect';
import { LIVING_TOGETHER, MARITAL_STATUS } from '../../../constants/applicant';
import { MARRIAGE_INFO_FIELD_NAME } from '../../../constants/fieldNames';
import { getValue } from '../../../constants/getFieldValues';
import { getPayload } from '../../../helpers/handlers';
import { i18n } from '../../../helpers/i18n';
import { ApplicantFields } from '../../../models/applicant';
import { ChildFields } from '../../../models/child';
import { PersonFields } from '../../../models/person';
import {
  getApplicant,
  getApplicantIsAddressCompleted
} from '../../../selectors/applicant';
import { shouldShowMarriageConfirmationPopover } from '../../../selectors/spouse';
import AddMarriageInfoPopover from '../../children/ConfirmationPopovers/AddMarriageInfoPopover';

interface StateProps {
  livingTogether: ApplicantFields['livingTogether'];
  shouldShowPopover: boolean;
  isVisible: boolean;
}

interface DispatchProps {
  onChange: ChangeEventHandler;
}

type Props = StateProps & DispatchProps;

function LiveTogether({
  livingTogether,
  onChange,
  shouldShowPopover,
  isVisible
}: Props): JSX.Element | null {
  const [isPopoverOpen, openPopover] = useState(false);
  if (!isVisible) return null;
  const { value, error } = livingTogether;

  const handleSpouseDataErase = () => {
    const event: unknown = {
      target: {
        name: MARRIAGE_INFO_FIELD_NAME.livingTogether,
        value: LIVING_TOGETHER.notLivingTogether
      }
    };
    onChange(event as ChangeEvent<HTMLInputElement>);
  };

  return (
    <div
      className="StepContentBlock LiveTogether"
      onBlur={() => openPopover(false)}
    >
      <Heading level={3} className="PguFormGrid__row LiveTogether__Title">
        <Trans>
          Проживаете ли вы совместно со вторым родителем одного или нескольких
          ваших детей?
        </Trans>
      </Heading>
      <div className="PguFormGrid__row">
        <Radio
          className="PguFormGrid_col"
          label={i18n._(t`Проживаю`)}
          name={MARRIAGE_INFO_FIELD_NAME.livingTogether}
          value={LIVING_TOGETHER.livingTogether}
          checked={value === LIVING_TOGETHER.livingTogether}
          onChange={onChange}
          error={error}
        />
      </div>
      <div className="PguFormGrid__row">
        <AddMarriageInfoPopover
          title={i18n._(t`Изменить сведения о совместном проживании`)}
          isPopoverOpen={isPopoverOpen}
          onPopoverClose={() => openPopover(false)}
          onConfirm={handleSpouseDataErase}
          onCancel={() => openPopover(false)}
        >
          <Radio
            className="PguFormGrid_col"
            label={i18n._(t`Не проживаю`)}
            name={MARRIAGE_INFO_FIELD_NAME.livingTogether}
            value={LIVING_TOGETHER.notLivingTogether}
            checked={value === LIVING_TOGETHER.notLivingTogether}
            onChange={shouldShowPopover ? () => openPopover(true) : onChange}
            error={error}
          />
        </AddMarriageInfoPopover>
      </div>
    </div>
  );
}

const mapStateToProps: (state: {
  applicant: ApplicantFields;
  spouse: PersonFields;
  children: ChildFields[];
}) => StateProps = createSelector(
  getApplicant,
  getApplicantIsAddressCompleted,
  shouldShowMarriageConfirmationPopover,
  (
    { livingTogether, married: maritalStatus },
    addressIsCompleted,
    shouldShowPopover
  ) => ({
    livingTogether,
    shouldShowPopover,
    isVisible: [
      getValue(maritalStatus) === MARITAL_STATUS.notMarried,
      addressIsCompleted
    ].every(Boolean)
  })
);

const mapDispatchToProps = (dispatch: Dispatch) => ({
  onChange(event) {
    dispatch({
      type: 'applicant/livingTogether/toggle',
      payload: getPayload(event)
    });
  }
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(LiveTogether);
